import React, { useState } from 'react';
import { m, AnimatePresence } from 'motion/react';
import { Plus, Minus, HelpCircle } from 'lucide-react';
import { FAQS, TRANSLATIONS } from '../constants';
import { useLanguage } from '../contexts/LanguageContext';

export const HowAustriaWorks: React.FC = () => {
  const { t } = useLanguage();
  const khT = t.knowledgeHub;
  const categories = khT.categories;
  const englishCategories = TRANSLATIONS.EN.knowledgeHub.categories;
  
  // Track the selected category by index, not by its (language-specific) label.
  // useLanguage() forces DE until this component has mounted, so a label captured
  // in useState's initializer ("Zusammenarbeit") would no longer exist in
  // `categories` once the language corrects to EN — leaving every tab/filter
  // lookup pointing at nothing and the FAQ list empty. An index stays valid
  // across languages.
  const [activeIndex, setActiveIndex] = useState(0);
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const currentCategoryIndex = Math.min(activeIndex, categories.length - 1);
  const activeCategory = categories[currentCategoryIndex];
  const englishCategory = englishCategories[currentCategoryIndex];
  
  // Use localized FAQs if available in the translation object
  const localizedFaqs = (khT as any).faqs as any[];
  const filteredFaqs = (localizedFaqs || FAQS).filter(faq => {
    if (localizedFaqs) {
      return faq.category === activeCategory;
    }
    return faq.category === englishCategory;
  });

  return (
    <section id="faq" className="py-24 bg-pstax-bg">
      <div className="container mx-auto px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 text-pstax-blue font-bold tracking-widest uppercase text-sm mb-4">
              <HelpCircle size={16} />
              <span>{khT.tag}</span>
            </div>
            <h2 className="text-4xl font-display font-bold mb-4">{khT.title}</h2>
            <p className="text-pstax-gray max-w-2xl mx-auto">
              {khT.sub}
            </p>
          </div>

          {/* Tabs */}
          <div className="mb-12">
            <div 
              className="flex flex-wrap justify-center gap-2 md:gap-3"
              role="tablist"
              aria-label="FAQ Categories"
            >
              {categories.map((cat: string, idx: number) => (
                <button
                  key={cat}
                  role="tab"
                  id={`tab-${idx}`}
                  aria-controls={`panel-${idx}`}
                  aria-selected={currentCategoryIndex === idx}
                  tabIndex={currentCategoryIndex === idx ? 0 : -1}
                  onClick={() => {
                    setActiveIndex(idx);
                    setOpenIndex(0); // Reset accordion on tab change
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowRight') {
                      const next = (idx + 1) % categories.length;
                      setActiveIndex(next);
                      setOpenIndex(0);
                      (document.getElementById(`tab-${next}`) as HTMLElement)?.focus();
                    } else if (e.key === 'ArrowLeft') {
                      const prev = (idx - 1 + categories.length) % categories.length;
                      setActiveIndex(prev);
                      setOpenIndex(0);
                      (document.getElementById(`tab-${prev}`) as HTMLElement)?.focus();
                    }
                  }}
                  className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all border outline-none ${
                    currentCategoryIndex === idx
                      ? 'bg-pstax-navy text-white border-pstax-navy shadow-lg shadow-pstax-navy/20 ring-2 ring-pstax-blue ring-offset-2'
                      : 'bg-white text-pstax-gray border-pstax-border hover:border-pstax-blue/40 hover:text-pstax-navy focus-visible:ring-2 focus-visible:ring-pstax-blue focus-visible:ring-offset-2'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div 
            className="space-y-4 min-h-[400px]"
            role="tabpanel"
            id={`panel-${currentCategoryIndex}`}
            aria-labelledby={`tab-${currentCategoryIndex}`}
          >
            {/* No cross-fade on category change: a keyed <AnimatePresence mode="wait">
                here deadlocked once the key actually changed (exit never completed,
                next panel never mounted), which froze the list on its first render. */}
            <div className="space-y-4">
                {filteredFaqs.map((faq, i) => (
                  <div 
                    key={faq.question} 
                    className={`border rounded-2xl transition-all ${openIndex === i ? 'bg-white border-pstax-blue/30 shadow-md' : 'bg-white/50 border-pstax-border hover:bg-white hover:border-pstax-blue/20'}`}
                  >
                    <button
                      onClick={() => setOpenIndex(openIndex === i ? null : i)}
                      className="w-full p-6 flex items-center justify-between text-left"
                      aria-expanded={openIndex === i}
                    >
                      <span className="font-display font-bold text-lg text-pstax-navy">{faq.question}</span>
                      <div className={`shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`} aria-hidden="true">
                        {openIndex === i ? <Minus size={20} className="text-pstax-blue" /> : <Plus size={20} className="text-pstax-gray" />}
                      </div>
                    </button>
                    
                    <AnimatePresence>
                      {openIndex === i && (
                        <m.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div 
                            className="px-6 py-6 border-t border-pstax-border/50 text-pstax-gray leading-relaxed whitespace-pre-line [&_a]:text-pstax-blue [&_a]:font-bold [&_a]:underline hover:[&_a]:text-pstax-navy [&_a]:decoration-solid [&_a]:underline-offset-4 transition-all"
                            dangerouslySetInnerHTML={{ __html: faq.answer }}
                          />
                        </m.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
            </div>
          </div>

          <div className="mt-12 p-8 glass rounded-3xl border-pstax-blue/20 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="font-display font-bold text-xl mb-1 text-pstax-navy">{khT.footerTitle}</p>
              <p className="text-pstax-gray text-sm">{khT.footerSub}</p>
            </div>
            <a href="#fit-check" className="btn-primary whitespace-nowrap" data-cta-location="section">
              {khT.footerCta}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
